import {
  createContext,
  FC,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";

import { useBlockScroll } from "../../../../../app/hooks/useBlockScroll";

import {
  BooleanPromise,
  PureVoidFunc,
  VoidBooleanPromise,
} from "../../../../../helpers/functions/types";
import {
  BORDER_STYLES,
  SLIDE_OVER_PADDING_STYLES,
} from "../../../detailedPages/styles";
import { SLIDEOVER_TRANSITION_STYLES } from "../../../Transition/constants";
import { SlideOverTitle } from "./SlideOverTitle";
import { SlideOverTabGroup, SlideOverTabGroupProps } from "./SlideOverTabGroup";
import { Button } from "../../../molecules/Button";
import { ButtonType } from "../../../molecules/types";
import {
  useInputsValid,
  ValidationProvider,
} from "../../../detailedPages/ValidationContext";
import { Closeable, SlideOverCloseContextType } from "./types";

const SLIDE_OVER_WRAPPER_STYLES: {
  [key: string]: string;
} = {
  productPage: "w-full",
  default: `py-2 xs:pb-5 pt-6 xs:pt-8 grow ${SLIDE_OVER_PADDING_STYLES} grid gap-4 empty:hidden`,
};

const SLIDE_OVER_TRANSITION_STYLES = "sm:translate-x-0 max-sm:translate-y-0";
const ENTER_FROM_SLIDE_OVER = "sm:translate-x-full max-sm:translate-y-full";

export const SlideOverCloseContext = createContext<SlideOverCloseContextType>({
  onClose: () => {},
});

type SubmitButtonProps = {
  onClick: VoidBooleanPromise;
  title?: string;
  disabled?: boolean;
  buttonType?: ButtonType;
};

// Rendered inside the slide-over's own ValidationProvider, so it sees the validity of the slide-over's inputs only.
const SubmitButton: FC<{
  buttonProps: SubmitButtonProps;
  isAccepting: boolean;
  blockSubmitOnInvalidInputs: boolean;
  onAccept: (onClick: VoidBooleanPromise) => void;
}> = ({ buttonProps, isAccepting, blockSubmitOnInvalidInputs, onAccept }) => {
  const isInputsValid = useInputsValid();
  return (
    <Button
      buttonType="primary"
      className={`grow basis-1/3${!buttonProps.buttonType ? " acceptButton" : ""}`}
      {...buttonProps}
      title={buttonProps.title ?? "Save changes"}
      disabled={
        buttonProps.disabled ||
        isAccepting ||
        (blockSubmitOnInvalidInputs && !isInputsValid)
      }
      onClick={() => onAccept(buttonProps.onClick)}
    />
  );
};

type SlideOverWrapperProps = Closeable & {
  initConfig?: PureVoidFunc;
  title: string;
  tabProps?: SlideOverTabGroupProps;
  buttonProps?: SubmitButtonProps;
  styleVariant?: keyof typeof SLIDE_OVER_WRAPPER_STYLES;
  declineText?: string;
  customFooter?: boolean;
  message?: string;
  /** Default true: invalid inputs (ValidationContext) disable submit. False: the format is only suggested. */
  blockSubmitOnInvalidInputs?: boolean;
};

export const SlideOverWrapper: FC<
  React.PropsWithChildren<SlideOverWrapperProps>
> = ({
  open,
  initConfig,
  onClose,
  children,
  title,
  tabProps,
  buttonProps,
  styleVariant = "default",
  declineText = "Cancel",
  customFooter = false,
  message,
  blockSubmitOnInvalidInputs = true,
}) => {
  const [isAccepting, setIsAccepting] = useState(false);

  const handleButton = async (onAccept?: () => BooleanPromise | void) => {
    if (!onAccept) onClose();
    else {
      setIsAccepting(true);
      const isSuccess = await onAccept();
      setIsAccepting(false);
      if (isSuccess) onClose();
    }
  };

  const preventableClose = useCallback(() => {
    if (!isAccepting) {
      onClose();
    }
  }, [isAccepting, onClose]);

  useBlockScroll(open, 500);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = (event: Event) => {
    //We are checking dAttribute because sometimes event click refer to the path tag inside the SVG and then ariaLabel would not be valid.
    const ariaLabel = (event.target as Element).getAttribute("aria-label");
    const dAttribute = (event.target as Element).getAttribute("d");

    if (
      wrapperRef.current &&
      !wrapperRef.current.contains(event.target as Node) &&
      ariaLabel !== "Close Notification" &&
      dAttribute !== "M6 18L18 6M6 6l12 12"
    ) {
      preventableClose();
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  });

  useEffect(() => {
    if (open) {
      initConfig?.();
      tabProps?.setActiveTabIndex(tabProps?.defaultIndex ?? 0);
    }
  }, [open]);

  return (
    <Transition show={open}>
      <Dialog
        as="div"
        className="relative z-20 mt-16"
        onClose={preventableClose}
      >
        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="fixed inset-y-0 right-0 flex max-w-full max-sm:pt-16 sm:pl-16">
              <TransitionChild
                as="div"
                enter={SLIDEOVER_TRANSITION_STYLES}
                enterFrom={ENTER_FROM_SLIDE_OVER}
                enterTo={SLIDE_OVER_TRANSITION_STYLES}
                leave={SLIDEOVER_TRANSITION_STYLES}
                leaveFrom={SLIDE_OVER_TRANSITION_STYLES}
                leaveTo={ENTER_FROM_SLIDE_OVER}
                className="pointer-events-auto w-screen max-w-full sm:max-w-lg max-sm:mt-auto"
              >
                <DialogPanel
                  className={`bg-stone-50 shadow-2xl sm:h-full max-sm:max-h-[calc(100vh-4rem)] dark:bg-stone-800 flex flex-col`}
                >
                  <ValidationProvider>
                    <div className="overflow-y-scroll shrink">
                      <SlideOverTitle
                        handleClose={onClose}
                        title={title}
                        className={
                          styleVariant === "productPage" ? "" : undefined
                        }
                        disabled={isAccepting}
                      >
                        {tabProps && <SlideOverTabGroup {...tabProps} />}
                      </SlideOverTitle>
                      <div className={SLIDE_OVER_WRAPPER_STYLES[styleVariant]}>
                        <SlideOverCloseContext.Provider value={{ onClose }}>
                          {children}
                        </SlideOverCloseContext.Provider>
                      </div>
                    </div>
                    {!customFooter && (
                      <div
                        className={`border-t py-5 ${BORDER_STYLES} ${SLIDE_OVER_PADDING_STYLES}`}
                      >
                        <div className="flex gap-4">
                          <Button
                            title={declineText}
                            buttonType="secondary"
                            onClick={onClose}
                            className="grow basis-1/3"
                            disabled={isAccepting}
                          />
                          {buttonProps && (
                            <SubmitButton
                              buttonProps={buttonProps}
                              isAccepting={isAccepting}
                              blockSubmitOnInvalidInputs={
                                blockSubmitOnInvalidInputs
                              }
                              onAccept={handleButton}
                            />
                          )}
                        </div>
                        {message && (
                          <p className="dark:text-white text-sm pt-4">
                            {message}
                          </p>
                        )}
                      </div>
                    )}
                  </ValidationProvider>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
